// src/utils/reviewToProgression.js
//
// Pass 77 — thin ADAPTER over the ONE canonical progression-evidence contract (progressionEvidence.js).
// It maps the canonical, AI-free, deterministic evidence into the legacy `progressionInput` shape that
// progressionEngine consumes. All authority (ranking, level, dedupe, sport isolation, provenance) lives in the
// contract. This file NO LONGER: reads aiReview, infers level from review count or terrain strings, fabricates a
// default "Balanced/Centered Stance" recommendation, or double-counts a review via aggregate + raw layers.

import { normalizeSportName } from './drillTemplates';
import { buildProgressionEvidence } from './progressionEvidence';

function normalizeReviewToProgressionArgs(arg1 = {}, arg2 = [], arg3 = {}) {
  if (Array.isArray(arg1)) {
    return { progressSummary: arg2 || {}, videos: arg1, options: arg3 || {} };
  }
  return {
    progressSummary: arg1 || {},
    videos: Array.isArray(arg2) ? arg2 : [],
    options: arg3 || {},
  };
}

// Fallback sport detection ONLY when the caller did not supply an explicit selected sport (backward-compat).
// When a selected sport IS known it is authoritative and no dominant-sport guessing occurs (guardrail 5).
function detectDominantSport(videos = []) {
  const reviewedVideos = (videos || [])
    .filter((video) => video?.status === 'reviewed')
    .sort((a, b) => {
      const aTime = a.reviewedAt?.seconds || a.createdAt?.seconds || 0;
      const bTime = b.reviewedAt?.seconds || b.createdAt?.seconds || 0;
      return bTime - aTime;
    });
  return reviewedVideos[0] ? normalizeSportName(reviewedVideos[0].sport) : '';
}

export function buildReviewToProgressionInput(arg1 = {}, arg2 = [], arg3 = {}) {
  const { progressSummary, videos, options } = normalizeReviewToProgressionArgs(arg1, arg2, arg3);

  // Explicit selected sport wins; else the sport already resolved upstream; else dominant-review fallback.
  const sport = normalizeSportName(options?.sport || progressSummary?.sport || '') || detectDominantSport(videos);
  if (!sport) return null;

  const evidence = buildProgressionEvidence({
    videos,
    lessonInstances: options?.lessonInstances || [],
    // reuse pre-built lesson signals (same sport) if the caller already computed them
    lessonSignals: progressSummary?.lessonSignals || null,
    sport,
  });

  const winner = evidence.winner;

  return {
    sport: evidence.sport,
    currentLevel: evidence.currentLevel,
    levelBasis: evidence.levelBasis,
    levelConfidence: evidence.levelConfidence,
    levelSourceSkillId: evidence.levelSourceSkillId,
    hasEvidence: evidence.hasEvidence,
    movementCategory: winner?.movementCategory || '',
    skillFocus: winner?.skillFocus || '',
    // canonical skillId the engine resolves the EXACT skill from (never a name/first-in-track guess)
    skillFocusSkillId: winner?.skillId || '',
    disciplineHint: winner?.disciplineHint || '',
    progressionPriority: winner ? (winner.score >= 5 ? 'primary' : 'secondary') : 'none',
    confidenceScore: evidence.confidenceScore,
    recommendationReason: evidence.recommendationReason,
    // provenance survives to the final reason (guardrail 7)
    sourceSignals: evidence.sourceSignals,
    categoryRanking: evidence.candidates.map((item) => ({
      movementCategory: item.movementCategory,
      skillFocus: item.skillFocus,
      disciplineHint: item.disciplineHint,
      score: item.score,
    })),
    unlockedBranchHints: evidence.unlockedBranchHints,
  };
}
